import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Clock, FileText } from 'lucide-react';
import { V1TermsModal } from './V1TermsModal';

interface V1EventFormWelcomeProps {
  form: any; // V1 form structure from public API
  onStart: () => void;
  primaryColor?: string;
  eventSlug?: string;
}

/**
 * V1 Event Form Welcome Page - FROZEN
 *
 * Shows introduction and requirements for V1 event forms in the public view.
 * This is used when public users or planners preview V1 forms.
 * V1 forms do NOT have pages, only single-page forms with sections.
 */
export const V1EventFormWelcome: React.FC<V1EventFormWelcomeProps> = ({
  form,
  onStart,
  primaryColor = '#3B82F6',
  eventSlug
}) => {
  const navigate = useNavigate();
  const [showTermsModal, setShowTermsModal] = useState(false);

  const handleBackToForms = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Navigate to forms list page using React Router
    navigate('/forms');
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    };

    return date.toLocaleDateString('en-US', options);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        {/* Back button */}
        <Link
          to="/forms"
          className="inline-flex items-center text-gray-600 hover:text-gray-900 bg-[#fffffff7] backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-sm mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Exit
        </Link>

        <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden">
          {/* Form image header if present */}
          {form.logoUrl && (
            <div className="relative h-48 bg-gray-100 overflow-hidden">
              {/* Blurred background image */}
              <div
                className="absolute inset-0 bg-cover bg-center blur-xl scale-110"
                style={{
                  backgroundImage: `url(${form.logoUrl})`,
                }}
              />

              {/* Overlay to soften the blurred background */}
              <div className="absolute inset-0 bg-white/30" />

              {/* Main image - fills either full height or width */}
              <div className="relative h-full w-full flex items-center justify-center">
                <img
                  src={form.logoUrl}
                  alt={form.name}
                  className="h-full w-full object-contain drop-shadow-lg"
                />
              </div>

              {/* Subtle gradient overlay at bottom */}
              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white/50 to-transparent" />
            </div>
          )}

          <div className="p-8">
            {/* Form icon and title */}
            <div className="flex items-start mb-6">
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center mr-4 flex-shrink-0"
                style={{ backgroundColor: `${primaryColor}20` }}
              >
                <FileText className="h-6 w-6" style={{ color: primaryColor }} />
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{form.name}</h1>

                {/* Form availability dates */}
                <div className="flex items-center text-sm text-gray-600">
                  <Clock className="h-4 w-4 mr-1" />
                  <span>
                    Available from {formatDate(form.startsAt)} to {formatDate(form.endsAt)}
                  </span>
                </div>
              </div>
            </div>

            {/* Already submitted message */}
            {form.hasSubmitted && form.lastSubmission && (
              <div className="mb-6 p-6 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Welcome back! 👋
                    </h3>
                    <p className="text-gray-700 mb-2">
                      You've already submitted this form.
                    </p>
                    <p className="text-sm text-gray-600 mb-4">
                      Confirmation #: <span className="font-mono font-semibold">{form.lastSubmission.confirmationNumber}</span>
                    </p>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      You can submit this form again if needed.
                    </div>
                  </div>
                  <div className="flex-shrink-0 ml-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Submitted
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* About section */}
            {form.description && (
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">About this form</h3>
                <div
                  className="prose prose-sm max-w-none text-gray-700"
                  dangerouslySetInnerHTML={{ __html: form.description }}
                />
              </div>
            )}

            {/* Instructions */}
            {form.instructions && (
              <div className="mb-8 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-start">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Instructions</h3>
                    <div
                      className="prose prose-sm max-w-none text-gray-700"
                      dangerouslySetInnerHTML={{ __html: form.instructions }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Submission requirements */}
            <div className="mb-8">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">What you'll need:</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mr-2" />
                  Basic contact information (name, email{form.options?.requirePhone === '1' && ', phone'})
                </li>
                {(form.options?.showCompanyOnForm === '1' || form.options?.requireCompany === '1') && (
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mr-2" />
                    Company information
                  </li>
                )}
                {(form.options?.showAddressOnForm === '1' || form.options?.requireAddress === '1') && (
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mr-2" />
                    Mailing address
                  </li>
                )}
                {(() => {
                  // For V1 forms, use surveyInfo.totalQuestions if available
                  const questionCount = form.surveyInfo?.totalQuestions || 0;
                  if (questionCount > 0) {
                    const estimatedMinutes = Math.ceil(questionCount * 0.5);
                    return (
                      <li className="flex items-center">
                        <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mr-2" />
                        {questionCount} question{questionCount !== 1 ? 's' : ''} (approximately {estimatedMinutes} minute{estimatedMinutes !== 1 ? 's' : ''})
                      </li>
                    );
                  }
                  return null;
                })()}
              </ul>
            </div>

            {/* Start/Submit button */}
            <div className="flex justify-center">
              <button
                onClick={onStart}
                className="inline-flex items-center px-8 py-3 text-white rounded-lg font-medium shadow-sm hover:shadow-md transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2"
                  style={{
                    backgroundColor: primaryColor,
                    focusRingColor: primaryColor
                  }}
                >
                  {form.hasSubmitted ? 'Submit Again' : (form.options?.callToAction || 'Start')}
                  <ArrowLeft className="h-5 w-5 ml-2 rotate-180" />
                </button>
              </div>

            {/* Terms link - small and out of the way */}
            {form.terms && (
              <div className="mt-6 text-center">
                <button
                  onClick={() => setShowTermsModal(true)}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  View Terms and Conditions
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Terms Modal */}
      {form.terms && (
        <V1TermsModal
          isOpen={showTermsModal}
          onClose={() => setShowTermsModal(false)}
          terms={form.terms}
          formName={form.name}
        />
      )}
    </div>
  );
};